import { type LandingBenefit, type LandingQuestion, type LandingSection, type LandingStep } from '@ui/landing'
import { CalculatorIcon, LayersIcon, PaperclipIcon, SendIcon, ShieldCheckIcon, WalletIcon } from 'lucide-react'

/**
 * Тексты страницы. Держим их отдельно от разметки: правки в них случаются чаще, чем правки
 * вёрстки, и так их видно списком, не разбирая JSX.
 */

/** Нумерация здесь не украшение: это порядок, в котором продавец доходит до первой цены. */
export const steps: LandingStep[] = [
  {
    title: 'Описываете задачу',
    text: 'Что за товар, сколько его, откуда забирать и на какую площадку отгружать, какие услуги нужны. Заполнять можно без входа: учётная запись понадобится только на последнем шаге.',
    note: 'Бесплатно, без входа',
  },
  {
    title: 'Заявку получают подходящие фулфилменты',
    text: 'Сначала её смотрит служба поддержки, обычно в течение дня. Потом заявку видят только те, кто работает по вашей схеме, возит на ваши площадки и подходит по географии — забирает товар там, где он лежит, или возит туда, куда вам нужно. В каталоге она не показывается и в поиск не попадает.',
    note: 'Никакой рассылки по всему каталогу',
  },
  {
    title: 'Они присылают свои цены',
    text: 'Каждый считает по вашим данным и отвечает ценой или уточняющими вопросами. Ответы собираются в разделе «Мои заявки», новые помечены.',
    note: 'Платы за ответ тоже нет',
  },
]

export const benefits: LandingBenefit[] = [
  {
    icon: SendIcon,
    title: 'Одна заявка вместо десяти переписок',
    text: 'Не нужно писать каждому в чат и повторять одно и то же. Условия задачи вы описываете один раз.',
  },
  {
    icon: CalculatorIcon,
    title: 'Цены под вашу задачу',
    text: 'Фулфилмент считает по вашим объёмам, габаритам и услугам, а не называет цифру «от» из прайса.',
  },
  {
    icon: LayersIcon,
    title: 'Ответы в одном месте',
    text: 'Все предложения лежат рядом в разделе «Мои заявки» — их видно вместе и удобно сравнивать.',
  },
  {
    icon: ShieldCheckIcon,
    title: 'Контакты не разлетаются',
    text: 'Телефон и мессенджеры видят только фулфилменты, которым заявка адресована. В каталог и поисковики они не попадают.',
  },
  {
    icon: PaperclipIcon,
    title: 'Файлы прикладываются к заявке',
    text: 'Фото товара, спецификация, макет этикетки — до пяти файлов. По ним считают точнее, чем по описанию словами.',
  },
  {
    icon: WalletIcon,
    title: 'Ничего не платите',
    text: 'Заявка, ответы фулфилментов и сам каталог бесплатны. Договариваетесь вы напрямую, Evirma в расчётах не участвует.',
  },
]

export const questions: LandingQuestion[] = [
  {
    question: 'Сколько стоит заявка?',
    answer:
      'Нисколько. Заявка, ответы фулфилментов и каталог бесплатны. Комиссии с вашей сделки Evirma тоже не берёт — договариваетесь напрямую.',
  },
  {
    question: 'Кто увидит мою заявку?',
    answer:
      'Только подходящие фулфилменты: те, кто работает по выбранной вами схеме, возит на ваши площадки и подходит по географии — забирает товар там, где он лежит, или возит в выбранные вами федеральные округа. Телефон и мессенджеры видят они же — в каталоге и в поиске заявка не показывается.',
  },
  {
    question: 'Нужно ли регистрироваться?',
    answer:
      'Заявку заполняете сразу, без входа. Учётная запись понадобится на последнем шаге — иначе ответы фулфилментов будет некуда прислать. Всё заполненное сохранится и дождётся вас: после регистрации вы вернётесь на тот же шаг. Регистрация бесплатна.',
  },
  {
    question: 'Где смотреть ответы?',
    answer:
      'В разделе «Мои заявки». Там же видно, что происходит с самой заявкой: сначала она на проверке, потом уходит фулфилментам, и их ответы появляются в той же карточке с пометкой «новый». Как быстро ответит фулфилмент, зависит от него — Evirma за него не отвечает.',
  },
  {
    question: 'А если ответов не будет?',
    answer:
      'Значит, условия слишком узкие. Расширьте географию и условия заявки: меньше ограничений по местам, больше площадок и схем работы, без требования двух отгрузок в день — и создайте заявку заново. Чем шире условия, тем больше фулфилментов её увидят. Новая заявка тоже бесплатна.',
  },
  {
    question: 'Нашёл исполнителя. Что делать с заявкой?',
    answer: 'Закройте её кнопкой «Закрыть заявку» — новые предложения перестанут приходить, а прежние останутся у вас.',
  },
]

/** Разделы, до которых доводит липкая полоса навигации. */
export const sections: LandingSection[] = [
  { id: 'how-it-works', label: 'Как это работает' },
  { id: 'benefits', label: 'Что даёт заявка' },
  { id: 'faq', label: 'Вопросы' },
]
